/**
 * AgentQualityView -- Inspecciona la memoria persistente de Argemiro (conv.chat_memory).
 * Vista basica F1 de la mision ARES-AGENT-QUALITY-001:
 *  - Stats globales
 *  - Lista de sessions
 *  - Detalle de mensajes por session
 *  - Busqueda por contenido
 *
 * Read-only. Las evoluciones F2-F5 agregaran compilacion de hechos, tests,
 * integracion con users/skills/capacitacion.
 *
 * @module presentation/views/AgentQualityView
 */

import { AgentMemoryApiClient } from '../../infrastructure/api/AgentMemoryApiClient.js';
import { fetchQueCambia } from '../../infrastructure/api/ApiBase.js';
import { AgentBadgeApiClient } from '../../infrastructure/api/AgentBadgeApiClient.js';
import { esAdminDeLaApi } from '../../domain/adminDeLaApi.js';
import { AppConfig } from '../../config.js';

// Deuda 4: era una copia del escape; ahora es el compartido.
import { esc } from '../shared/texto.js';
import { regresoHtml } from '../shared/regreso.js';
import { formatearFecha } from '../shared/fechaColombiana.js';

let currentTab = 'convos';
let selectedSessionId = null;
let containerRef = null;

// UIA-T5: estado del tab Conversaciones (cache en memoria, filtros client-side)
let convosCache = [];
let convoFilters = { text: '', channel: 'all', from: '', to: '' };
let threadCache = { sessionId: null, msgs: [] };
let showTechnical = false;

function fmtDate(iso) {
  if (!iso) return '--';
  try {
    const d = new Date(iso);
    return formatearFecha(d, {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit'
    }, { parte: 'todo' });
  } catch { return iso; }
}

function typeBadge(t) {
  const map = {
    human: { color: '#58a6ff', label: 'Usuario' },
    ai: { color: '#3fb950', label: 'Argemiro' },
    tool: { color: '#d29922', label: 'Tool' },
    system: { color: '#bc8cff', label: 'System' }
  };
  const cfg = map[t] || { color: '#8b949e', label: t || 'unknown' };
  return `<span style="background:${cfg.color}22;color:${cfg.color};padding:2px 8px;border-radius:4px;font-size:0.875rem;font-weight:600">${cfg.label}</span>`;
}

function msgBgColor(t) {
  const bg = { human: '#0d2540', ai: '#0d2818', tool: '#2a1f08', system: '#2a1a3a' };
  return bg[t] || '#161b22';
}

function msgBorderColor(t) {
  const colors = { human: '#58a6ff', ai: '#3fb950', tool: '#d29922', system: '#bc8cff' };
  return colors[t] || '#30363d';
}

async function loadStats() {
  const panel = document.getElementById('aq-content');
  if (!panel) return;
  panel.innerHTML = '<div style="padding:20px;color:#8b949e">Cargando estadisticas...</div>';

  try {
    const res = await AgentMemoryApiClient.getStats();
    if (!res.ok) throw new Error(res.error || 'Error cargando stats');
    const body = res.data || {};
    const t = body.totals || {};
    const topSessions = body.top_sessions || [];
    const recent = body.recent_activity_7d || [];

    panel.innerHTML = `
      <div style="padding:16px">
        <h3 style="margin:0 0 12px 0;font-size:1rem">Resumen global</h3>
        <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(160px,1fr));gap:12px;margin-bottom:20px">
          <div class="aq-stat-card"><div class="aq-stat-label">Sessions</div><div class="aq-stat-value">${t.total_sessions ?? 0}</div></div>
          <div class="aq-stat-card"><div class="aq-stat-label">Mensajes totales</div><div class="aq-stat-value">${t.total_messages ?? 0}</div></div>
          <div class="aq-stat-card"><div class="aq-stat-label">Del usuario</div><div class="aq-stat-value" style="color:#58a6ff">${t.total_human ?? 0}</div></div>
          <div class="aq-stat-card"><div class="aq-stat-label">De Argemiro</div><div class="aq-stat-value" style="color:#3fb950">${t.total_ai ?? 0}</div></div>
        </div>
        <div style="font-size:0.875rem;color:#8b949e;margin-bottom:16px">
          Primer mensaje: <strong>${fmtDate(t.oldest_message)}</strong> &middot;
          Ultimo: <strong>${fmtDate(t.newest_message)}</strong>
        </div>

        <h3 style="margin:0 0 8px 0;font-size:0.95rem">Top 5 sessions mas activas</h3>
        <table class="aq-table" style="margin-bottom:20px">
          <thead><tr><th>Session ID</th><th style="text-align:right">Mensajes</th></tr></thead>
          <tbody>
            ${topSessions.map(s => `
              <tr><td><code style="font-size:0.875rem">${esc(s.session_id)}</code></td><td style="text-align:right;font-weight:600">${s.messages}</td></tr>
            `).join('') || '<tr><td colspan="2" style="color:#484f58">Sin sessions</td></tr>'}
          </tbody>
        </table>

        <h3 style="margin:0 0 8px 0;font-size:0.95rem">Actividad ultimos 7 dias</h3>
        <table class="aq-table">
          <thead><tr><th>Dia</th><th style="text-align:right">Mensajes</th></tr></thead>
          <tbody>
            ${recent.map(r => `
              <tr><td>${fmtDate(r.day).split(',')[0]}</td><td style="text-align:right">${r.messages}</td></tr>
            `).join('') || '<tr><td colspan="2" style="color:#484f58">Sin actividad</td></tr>'}
          </tbody>
        </table>
      </div>
    `;
  } catch (err) {
    panel.innerHTML = `<div style="padding:20px;color:#f85149">Error: ${esc(err.message)}<br><br>
      <small style="color:#8b949e">Posible causa: la connection string AgentHub no esta configurada en NexoAPI,
      o el usuario postgres no tiene permisos SELECT en schema conv. Ver Docs/AGENT_QUALITY_SETUP.md</small>
    </div>`;
  }
}

async function loadSessions() {
  const panel = document.getElementById('aq-content');
  if (!panel) return;
  panel.innerHTML = '<div style="padding:20px;color:#8b949e">Cargando sessions...</div>';

  try {
    const res = await AgentMemoryApiClient.listSessions(50, 0);
    if (!res.ok) throw new Error(res.error || 'Error cargando sessions');
    const body = res.data || {};
    const sessions = body.data || [];
    panel.innerHTML = `
      <div style="padding:16px">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px">
          <h3 style="margin:0;font-size:1rem">Sessions (${body.total ?? 0} total)</h3>
          <button id="aq-refresh-sessions" class="aq-btn">Refrescar</button>
        </div>
        <table class="aq-table">
          <thead><tr>
            <th>Session ID</th>
            <th style="text-align:right">Total</th>
            <th style="text-align:right">User</th>
            <th style="text-align:right">AI</th>
            <th>Ultima</th>
            <th></th>
          </tr></thead>
          <tbody>
            ${sessions.map(s => `
              <tr>
                <td><code style="font-size:0.875rem">${esc(s.session_id)}</code></td>
                <td style="text-align:right;font-weight:600">${s.message_count}</td>
                <td style="text-align:right;color:#58a6ff">${s.human_count}</td>
                <td style="text-align:right;color:#3fb950">${s.ai_count}</td>
                <td style="font-size:0.875rem;color:#8b949e">${fmtDate(s.last_message_at)}</td>
                <td><button class="aq-btn aq-btn-view" data-sid="${esc(s.session_id)}">Ver</button></td>
              </tr>
            `).join('') || '<tr><td colspan="6" style="color:#484f58;text-align:center">Sin sessions</td></tr>'}
          </tbody>
        </table>
      </div>
    `;

    document.getElementById('aq-refresh-sessions')?.addEventListener('click', loadSessions);
    panel.querySelectorAll('.aq-btn-view').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const sid = e.currentTarget.getAttribute('data-sid');
        selectedSessionId = sid;
        currentTab = 'messages';
        renderTabs();
        loadMessages(sid);
      });
    });
  } catch (err) {
    panel.innerHTML = `<div style="padding:20px;color:#f85149">Error: ${esc(err.message)}</div>`;
  }
}

async function loadMessages(sessionId) {
  const panel = document.getElementById('aq-content');
  if (!panel) return;
  panel.innerHTML = `<div style="padding:20px;color:#8b949e">Cargando mensajes de session...</div>`;

  try {
    const res = await AgentMemoryApiClient.getMessages(sessionId, 200);
    if (!res.ok) throw new Error(res.error || 'Error cargando mensajes');
    const body = res.data || {};
    const msgs = body.data || [];
    panel.innerHTML = `
      <div style="padding:16px">
        <div style="display:flex;gap:12px;align-items:center;margin-bottom:12px">
          <button id="aq-back" class="aq-btn">&larr; Sessions</button>
          <h3 style="margin:0;font-size:0.95rem"><code>${esc(sessionId)}</code></h3>
          <span style="color:#8b949e;font-size:0.875rem">${body.count} mensajes</span>
        </div>
        <div style="display:flex;flex-direction:column;gap:8px">
          ${msgs.map(m => `
            <div style="background:${msgBgColor(m.msg_type)};padding:10px 12px;border-radius:6px;border-left:3px solid ${msgBorderColor(m.msg_type)}">
              <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:4px">
                ${typeBadge(m.msg_type)}
                <span style="font-size:0.875rem;color:#8b949e">#${m.id} &middot; ${fmtDate(m.created_at)}</span>
              </div>
              <div style="font-size:0.875rem;white-space:pre-wrap;word-break:break-word">${esc(m.content)}</div>
            </div>
          `).join('') || '<div style="color:#484f58">Sin mensajes</div>'}
        </div>
      </div>
    `;

    document.getElementById('aq-back')?.addEventListener('click', () => {
      selectedSessionId = null;
      currentTab = 'sessions';
      renderTabs();
      loadSessions();
    });
  } catch (err) {
    panel.innerHTML = `<div style="padding:20px;color:#f85149">Error: ${esc(err.message)}</div>`;
  }
}

async function loadSearch() {
  const panel = document.getElementById('aq-content');
  if (!panel) return;
  panel.innerHTML = `
    <div style="padding:16px">
      <h3 style="margin:0 0 12px 0;font-size:1rem">Buscar en memoria</h3>
      <div style="display:flex;gap:8px;margin-bottom:16px">
        <input id="aq-search-input" type="text" placeholder="Texto a buscar en mensajes..." style="flex:1;padding:8px 12px;background:#0d1117;border:1px solid #30363d;border-radius:4px;color:#c9d1d9">
        <button id="aq-search-btn" class="aq-btn">Buscar</button>
      </div>
      <div id="aq-search-results"></div>
    </div>
  `;

  const input = document.getElementById('aq-search-input');
  const btn = document.getElementById('aq-search-btn');
  const results = document.getElementById('aq-search-results');

  const doSearch = async () => {
    const q = input.value.trim();
    if (!q) { results.innerHTML = '<div style="color:#8b949e">Ingresa texto a buscar</div>'; return; }
    results.innerHTML = '<div style="color:#8b949e">Buscando...</div>';
    try {
      const res = await AgentMemoryApiClient.search(q, 50);
      if (!res.ok) throw new Error(res.error || 'Error en busqueda');
      const body = res.data || {};
      const msgs = body.data || [];
      results.innerHTML = msgs.length === 0
        ? `<div style="color:#484f58">Sin coincidencias para "${esc(q)}"</div>`
        : `<div style="display:flex;flex-direction:column;gap:8px">
             ${msgs.map(m => `
               <div style="background:${msgBgColor(m.msg_type)};padding:10px 12px;border-radius:6px;border-left:3px solid ${msgBorderColor(m.msg_type)}">
                 <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:4px;gap:8px">
                   ${typeBadge(m.msg_type)}
                   <code style="font-size:0.875rem;color:#8b949e;flex:1;text-align:right">${esc(m.session_id)}</code>
                   <span style="font-size:0.875rem;color:#8b949e">#${m.id}</span>
                 </div>
                 <div style="font-size:0.875rem;white-space:pre-wrap">${esc(m.content)}</div>
               </div>
             `).join('')}
           </div>`;
    } catch (err) {
      results.innerHTML = `<div style="color:#f85149">Error: ${esc(err.message)}</div>`;
    }
  };

  btn.addEventListener('click', doSearch);
  input.addEventListener('keydown', (e) => { if (e.key === 'Enter') doSearch(); });
  input.focus();
}

// ============================================================
// UIA-T5: Conversaciones — auditoria legible de Argemiro
// Sesiones agrupadas por canal (WhatsApp/Panel/Inventario) con
// filtros por texto/canal/fecha y hilo pregunta-respuesta.
// Solo lectura sobre endpoints agent-memory existentes.
// ============================================================

const CHANNELS = {
  whatsapp: { label: 'WhatsApp', color: '#25d366', icon: '📱' },
  panel: { label: 'Panel', color: '#58a6ff', icon: '🖥' },
  inventario: { label: 'Inventario IA', color: '#d29922', icon: '📦' },
  otro: { label: 'Otro', color: '#8b949e', icon: '⚙' }
};

function channelOf(sessionId) {
  const sid = String(sessionId || '');
  if (sid.startsWith('wa-')) return 'whatsapp';
  if (sid.startsWith('=panel-') || sid.startsWith('panel-')) return 'panel';
  if (sid.startsWith('=inv-analyze-') || sid.startsWith('inv-analyze-')) return 'inventario';
  return 'otro';
}

function channelBadge(ch) {
  const cfg = CHANNELS[ch] || CHANNELS.otro;
  return `<span style="background:${cfg.color}22;color:${cfg.color};padding:2px 8px;border-radius:4px;font-size:0.875rem;font-weight:600">${cfg.icon} ${cfg.label}</span>`;
}

function formatPhone(digits) {
  const d = String(digits || '').replace(/\D/g, '');
  if (!d) return 'sin numero';
  if (d.length === 12 && d.startsWith('57')) return `+57 ${d.slice(2, 5)} ${d.slice(5, 8)} ${d.slice(8)}`;
  return `+${d}`;
}

function sessionLabel(sessionId) {
  const sid = String(sessionId || '');
  const ch = channelOf(sid);
  if (ch === 'whatsapp') return formatPhone(sid.slice(3));
  if (ch === 'panel') return sid.replace(/^=?panel-/, 'sesion panel ');
  if (ch === 'inventario') return sid.replace(/^=?inv-analyze-/, 'analisis ');
  return sid;
}

function extractCapability(content) {
  try {
    const arr = JSON.parse(content);
    if (Array.isArray(arr) && arr.length && arr[0] && arr[0].capability_used) return arr[0].capability_used;
  } catch { /* content no es JSON de tool */ }
  return null;
}

function isTechnicalMsg(m) {
  if (m.msg_type === 'tool' || m.msg_type === 'system') return true;
  const c = String(m.content ?? '').trim();
  return m.msg_type === 'ai' && (c === '' || c === '[]');
}

function fmtDayChip(iso) {
  try {
    return formatearFecha(iso, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return String(iso).slice(0, 10); }
}

function fmtHour(iso) {
  try {
    return formatearFecha(iso, { hour: '2-digit', minute: '2-digit' }, { parte: 'hora' });
  } catch { return ''; }
}

function convoMatchesFilters(s) {
  const ch = channelOf(s.session_id);
  if (convoFilters.channel !== 'all' && ch !== convoFilters.channel) return false;
  if (convoFilters.text) {
    const t = convoFilters.text.toLowerCase().replace(/\s/g, '');
    const hay = (String(s.session_id) + sessionLabel(s.session_id)).toLowerCase().replace(/\s/g, '');
    if (!hay.includes(t)) return false;
  }
  if (convoFilters.from) {
    const from = new Date(convoFilters.from + 'T00:00:00');
    if (new Date(s.last_message_at) < from) return false;
  }
  if (convoFilters.to) {
    const to = new Date(convoFilters.to + 'T23:59:59');
    if (new Date(s.first_message_at) > to) return false;
  }
  return true;
}

function renderConvoList() {
  const listEl = document.getElementById('aq-convo-list');
  if (!listEl) return;
  const filtered = convosCache.filter(convoMatchesFilters);
  const countEl = document.getElementById('aq-convo-count');
  if (countEl) countEl.textContent = `${filtered.length} de ${convosCache.length} conversaciones`;

  listEl.innerHTML = filtered.map(s => `
    <div class="aq-convo-card" data-sid="${esc(s.session_id)}" style="background:#161b22;border:1px solid #30363d;border-radius:6px;padding:12px;cursor:pointer;display:flex;align-items:center;gap:12px">
      <div style="flex:1;min-width:0">
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:4px;flex-wrap:wrap">
          ${channelBadge(channelOf(s.session_id))}
          <strong style="font-size:0.88rem">${esc(sessionLabel(s.session_id))}</strong>
        </div>
        <div style="font-size:0.875rem;color:#8b949e">
          <span style="color:#58a6ff">${s.human_count} preguntas</span> &middot;
          <span style="color:#3fb950">${s.ai_count} respuestas</span> &middot;
          ${fmtDate(s.first_message_at)} → ${fmtDate(s.last_message_at)}
        </div>
      </div>
      <span style="color:#484f58;font-size:1.1rem">›</span>
    </div>
  `).join('') || '<div style="color:#484f58;padding:20px;text-align:center">Ninguna conversacion coincide con los filtros</div>';

  listEl.querySelectorAll('.aq-convo-card').forEach(card => {
    card.addEventListener('click', () => loadConvoThread(card.getAttribute('data-sid')));
  });
}

async function loadConvos() {
  const panel = document.getElementById('aq-content');
  if (!panel) return;
  panel.innerHTML = '<div style="padding:20px;color:#8b949e">Cargando conversaciones...</div>';

  try {
    const res = await AgentMemoryApiClient.listSessions(500, 0);
    if (!res.ok) throw new Error(res.error || 'Error cargando conversaciones');
    convosCache = res.data?.data || [];

    panel.innerHTML = `
      <div style="padding:16px">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:4px;flex-wrap:wrap;gap:8px">
          <h3 style="margin:0;font-size:1rem">Conversaciones de Argemiro</h3>
          <span id="aq-convo-count" style="font-size:0.875rem;color:#8b949e"></span>
        </div>
        <p style="font-size:0.875rem;color:#8b949e;margin:0 0 12px 0">Auditoria de que se le pregunto y que respondio, por usuario y canal. Solo lectura.</p>
        <div style="display:flex;gap:8px;margin-bottom:14px;flex-wrap:wrap">
          <input id="aq-convo-text" type="text" placeholder="Telefono o sesion..." value="${esc(convoFilters.text)}"
            style="flex:1;min-width:160px;padding:7px 10px;background:#0d1117;border:1px solid #30363d;border-radius:4px;color:#c9d1d9;font-size:0.875rem">
          <select id="aq-convo-channel" style="padding:7px 10px;background:#0d1117;border:1px solid #30363d;border-radius:4px;color:#c9d1d9;font-size:0.875rem">
            <option value="all">Todos los canales</option>
            <option value="whatsapp">WhatsApp</option>
            <option value="panel">Panel</option>
            <option value="inventario">Inventario IA</option>
            <option value="otro">Otro</option>
          </select>
          <input id="aq-convo-from" type="date" value="${esc(convoFilters.from)}" title="Desde"
            style="padding:7px 10px;background:#0d1117;border:1px solid #30363d;border-radius:4px;color:#c9d1d9;font-size:0.875rem">
          <input id="aq-convo-to" type="date" value="${esc(convoFilters.to)}" title="Hasta"
            style="padding:7px 10px;background:#0d1117;border:1px solid #30363d;border-radius:4px;color:#c9d1d9;font-size:0.875rem">
          <button id="aq-convo-clear" class="aq-btn" title="Limpiar filtros">Limpiar</button>
        </div>
        <div id="aq-convo-list" style="display:flex;flex-direction:column;gap:8px"></div>
      </div>
    `;

    const sel = document.getElementById('aq-convo-channel');
    if (sel) sel.value = convoFilters.channel;

    document.getElementById('aq-convo-text')?.addEventListener('input', (e) => {
      convoFilters.text = e.target.value.trim(); renderConvoList();
    });
    sel?.addEventListener('change', (e) => { convoFilters.channel = e.target.value; renderConvoList(); });
    document.getElementById('aq-convo-from')?.addEventListener('change', (e) => {
      convoFilters.from = e.target.value; renderConvoList();
    });
    document.getElementById('aq-convo-to')?.addEventListener('change', (e) => {
      convoFilters.to = e.target.value; renderConvoList();
    });
    document.getElementById('aq-convo-clear')?.addEventListener('click', () => {
      convoFilters = { text: '', channel: 'all', from: '', to: '' };
      loadConvos();
    });

    renderConvoList();
  } catch (err) {
    panel.innerHTML = `<div style="padding:20px;color:#f85149">Error: ${esc(err.message)}<br><br>
      <small style="color:#8b949e">Posible causa: la connection string AgentHub no esta configurada en NexoAPI. Ver Docs/AGENT_QUALITY_SETUP.md</small></div>`;
  }
}

function renderThread() {
  const wrap = document.getElementById('aq-thread');
  if (!wrap) return;
  const msgs = threadCache.msgs;
  const visible = showTechnical ? msgs : msgs.filter(m => !isTechnicalMsg(m));
  const visCount = document.getElementById('aq-thread-count');
  if (visCount) visCount.textContent = `${visible.length} de ${msgs.length} mensajes`;

  let lastDay = '';
  wrap.innerHTML = visible.map(m => {
    const day = String(m.created_at || '').slice(0, 10);
    const dayChip = day !== lastDay
      ? `<div style="text-align:center;margin:10px 0 4px"><span style="background:#21262d;color:#8b949e;padding:3px 12px;border-radius:12px;font-size:0.875rem">${esc(fmtDayChip(m.created_at))}</span></div>`
      : '';
    lastDay = day;

    if (isTechnicalMsg(m)) {
      const cap = m.msg_type === 'tool' ? extractCapability(m.content) : null;
      return `${dayChip}
        <div style="align-self:center;max-width:90%;background:#161b22;border:1px dashed #30363d;border-radius:6px;padding:6px 10px;margin:2px 0">
          <div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap">
            ${typeBadge(m.msg_type)}
            ${cap ? `<span style="background:#a371f722;color:#a371f7;padding:2px 8px;border-radius:4px;font-size:0.875rem;font-weight:600">⚙ ${esc(cap)}</span>` : ''}
            <span style="font-size:0.875rem;color:#484f58">${esc(fmtHour(m.created_at))}</span>
          </div>
          <div style="font-size:0.875rem;color:#8b949e;white-space:pre-wrap;word-break:break-word;max-height:120px;overflow-y:auto;margin-top:4px">${esc(String(m.content ?? '').slice(0, 600))}</div>
        </div>`;
    }

    const isHuman = m.msg_type === 'human';
    return `${dayChip}
      <div style="align-self:${isHuman ? 'flex-start' : 'flex-end'};max-width:78%;background:${isHuman ? '#0d2540' : '#0d2818'};border:1px solid ${isHuman ? '#1f4066' : '#1f4a2c'};border-radius:${isHuman ? '2px 10px 10px 10px' : '10px 2px 10px 10px'};padding:8px 12px;margin:2px 0">
        <div style="font-size:0.875rem;color:${isHuman ? '#58a6ff' : '#3fb950'};font-weight:600;margin-bottom:2px">${isHuman ? 'Usuario' : 'Argemiro'}</div>
        <div style="font-size:0.875rem;white-space:pre-wrap;word-break:break-word">${esc(m.content)}</div>
        <div style="font-size:0.875rem;color:#484f58;text-align:right;margin-top:3px">${esc(fmtHour(m.created_at))}</div>
      </div>`;
  }).join('') || '<div style="color:#484f58;padding:20px;text-align:center">Sin mensajes visibles (activa "Pasos tecnicos" para ver todo)</div>';

  wrap.scrollTop = wrap.scrollHeight;
}

async function loadConvoThread(sessionId) {
  const panel = document.getElementById('aq-content');
  if (!panel) return;
  panel.innerHTML = '<div style="padding:20px;color:#8b949e">Cargando hilo...</div>';

  try {
    const res = await AgentMemoryApiClient.getMessages(sessionId, 500);
    if (!res.ok) throw new Error(res.error || 'Error cargando hilo');
    threadCache = { sessionId, msgs: res.data?.data || [] };

    panel.innerHTML = `
      <div style="padding:16px">
        <div style="display:flex;gap:10px;align-items:center;margin-bottom:12px;flex-wrap:wrap">
          <button id="aq-convo-back" class="aq-btn">&larr; Conversaciones</button>
          ${channelBadge(channelOf(sessionId))}
          <strong style="font-size:0.9rem">${esc(sessionLabel(sessionId))}</strong>
          <span id="aq-thread-count" style="font-size:0.875rem;color:#8b949e"></span>
          <label style="margin-left:auto;display:flex;align-items:center;gap:6px;font-size:0.875rem;color:#8b949e;cursor:pointer">
            <input id="aq-thread-tech" type="checkbox" ${showTechnical ? 'checked' : ''}> Pasos tecnicos
          </label>
        </div>
        <div id="aq-thread" style="display:flex;flex-direction:column;gap:2px;background:#0a0e14;border:1px solid #21262d;border-radius:8px;padding:14px;max-height:65vh;overflow-y:auto"></div>
      </div>
    `;

    document.getElementById('aq-convo-back')?.addEventListener('click', () => {
      threadCache = { sessionId: null, msgs: [] };
      loadConvos();
    });
    document.getElementById('aq-thread-tech')?.addEventListener('change', (e) => {
      showTechnical = e.target.checked;
      renderThread();
    });

    renderThread();
  } catch (err) {
    panel.innerHTML = `<div style="padding:20px;color:#f85149">Error: ${esc(err.message)}</div>`;
  }
}

function renderTabs() {
  const tabsBar = document.getElementById('aq-tabs');
  if (!tabsBar) return;
  const tabs = [
    { id: 'convos', label: 'Conversaciones' },
    { id: 'stats', label: 'Stats' },
    { id: 'sessions', label: 'Sessions' },
    { id: 'facts', label: 'Facts' },
    { id: 'tests', label: 'Tests' },
    { id: 'search', label: 'Buscar' }
  ];
  tabsBar.innerHTML = tabs.map(t =>
    `<button class="aq-tab ${currentTab === t.id ? 'active' : ''}" data-tab="${t.id}">${t.label}</button>`
  ).join('');
  tabsBar.querySelectorAll('.aq-tab').forEach(b => {
    b.addEventListener('click', (e) => {
      currentTab = e.currentTarget.getAttribute('data-tab');
      selectedSessionId = null;
      renderTabs();
      loadCurrentTab();
    });
  });
}

function loadCurrentTab() {
  if (currentTab === 'convos') loadConvos();
  else if (currentTab === 'stats') loadStats();
  else if (currentTab === 'sessions') loadSessions();
  else if (currentTab === 'facts') loadFacts();
  else if (currentTab === 'tests') loadTests();
  else if (currentTab === 'search') loadSearch();
}

// ============================================================
// F3: Facts compilados + Tests de recall
// ============================================================

const FACT_TYPE_LABELS = {
  name: { label: 'Nombre', color: '#58a6ff', icon: '👤' },
  role: { label: 'Rol', color: '#a371f7', icon: '💼' },
  preference: { label: 'Preferencia', color: '#3fb950', icon: '⭐' },
  skill: { label: 'Skill', color: '#d29922', icon: '🛠' },
  project: { label: 'Proyecto', color: '#f97583', icon: '📁' },
  fact: { label: 'Hecho', color: '#8b949e', icon: '📌' },
  goal: { label: 'Meta', color: '#bc8cff', icon: '🎯' },
  context: { label: 'Contexto', color: '#39d353', icon: '🌐' }
};

function factTypeBadge(ft) {
  const cfg = FACT_TYPE_LABELS[ft] || { label: ft, color: '#8b949e', icon: '?' };
  return `<span style="background:${cfg.color}22;color:${cfg.color};padding:2px 8px;border-radius:4px;font-size:0.875rem;font-weight:600">${cfg.icon} ${cfg.label}</span>`;
}

function confidenceBar(c) {
  const pct = Math.round((c || 0) * 100);
  const color = pct >= 80 ? '#3fb950' : pct >= 50 ? '#d29922' : '#f85149';
  return `<div style="display:flex;align-items:center;gap:6px">
    <div style="flex:1;height:6px;background:#21262d;border-radius:3px;overflow:hidden">
      <div style="width:${pct}%;height:100%;background:${color};border-radius:3px"></div>
    </div>
    <span style="font-size:0.875rem;color:${color};min-width:32px;text-align:right">${pct}%</span>
  </div>`;
}

async function loadFacts() {
  const panel = document.getElementById('aq-content');
  if (!panel) return;
  panel.innerHTML = '<div style="padding:20px;color:#8b949e">Cargando hechos compilados...</div>';

  try {
    const [factsRes, summaryRes] = await Promise.all([
      AgentMemoryApiClient.getFacts(),
      AgentMemoryApiClient.getFactsSummary()
    ]);
    if (!factsRes.ok) throw new Error(factsRes.error || 'Error cargando facts');
    const facts = factsRes.data?.data || [];
    const summary = summaryRes.ok ? (summaryRes.data?.data || []) : [];

    panel.innerHTML = `
      <div style="padding:16px">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px">
          <h3 style="margin:0;font-size:1rem">Hechos compilados (${facts.length})</h3>
          <button id="aq-compile-btn" class="aq-btn" style="background:#238636;border-color:#2ea043">Compilar ahora</button>
        </div>

        ${summary.length > 0 ? `
          <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));gap:12px;margin-bottom:16px">
            ${summary.map(s => `
              <div class="aq-stat-card">
                <div style="font-weight:600;margin-bottom:4px">${esc(s.user_name || s.external_id)}</div>
                <div style="font-size:0.875rem;color:#8b949e">${s.total_facts} hechos &middot; ${s.fact_types} tipos &middot; conf ${Math.round((s.avg_confidence || 0) * 100)}%</div>
                <div style="font-size:0.875rem;color:#484f58;margin-top:4px">Ultimo: ${fmtDate(s.last_active)}</div>
              </div>
            `).join('')}
          </div>
        ` : ''}

        ${facts.length > 0 ? `
          <table class="aq-table">
            <thead><tr>
              <th>Tipo</th><th>Key</th><th>Valor</th><th style="width:120px">Confianza</th><th>Ultima mencion</th>
            </tr></thead>
            <tbody>
              ${facts.map(f => `
                <tr>
                  <td>${factTypeBadge(f.fact_type)}</td>
                  <td><code style="font-size:0.875rem">${esc(f.fact_key)}</code></td>
                  <td style="font-weight:500">${esc(f.fact_value)}</td>
                  <td>${confidenceBar(f.confidence)}</td>
                  <td style="font-size:0.875rem;color:#8b949e">${fmtDate(f.last_mentioned_at)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        ` : '<div style="color:#484f58;padding:20px;text-align:center">Sin hechos compilados. Click "Compilar ahora" para extraer.</div>'}
      </div>
    `;

    document.getElementById('aq-compile-btn')?.addEventListener('click', async () => {
      const btn = document.getElementById('aq-compile-btn');
      btn.textContent = 'Compilando...';
      btn.disabled = true;
      try {
        const r = await fetchQueCambia('https://n8n.nexodigital360.com/webhook/agent/memory/compile', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ external_id: 'panel-user', user_name: 'Panel User', session_limit: 5 })
        });
        const data = await r.json().catch(() => ({}));
        btn.textContent = data.upserted != null ? 'OK: ' + data.upserted + ' facts' : 'Completado';
        setTimeout(() => loadFacts(), 1500);
      } catch (err) {
        btn.textContent = 'Error';
        btn.style.background = '#da3633';
      }
    });
  } catch (err) {
    panel.innerHTML = `<div style="padding:20px;color:#f85149">Error: ${esc(err.message)}</div>`;
  }
}

async function loadTests() {
  const panel = document.getElementById('aq-content');
  if (!panel) return;

  const testCases = [
    { id: 'recall_name', label: 'Recuerda nombre del usuario', question: 'como me llamo?', expected_fact: 'name', description: 'El agente debe responder con el nombre del usuario registrado en facts' },
    { id: 'recall_role', label: 'Recuerda rol del usuario', question: 'cual es mi rol o que hago?', expected_fact: 'role', description: 'El agente debe mencionar el rol profesional del usuario' },
    { id: 'recall_project', label: 'Recuerda proyecto activo', question: 'en que proyecto estoy trabajando?', expected_fact: 'project', description: 'El agente debe mencionar el proyecto o mision activa' },
    { id: 'recall_preference', label: 'Recuerda preferencias', question: 'cual es mi artefacto favorito?', expected_fact: 'preference', description: 'El agente debe mencionar la preferencia registrada' },
    { id: 'session_isolation', label: 'Aislamiento de sesiones', question: 'que sabes de mi? (session nueva)', expected_fact: null, description: 'Con session_id diferente, el agente NO debe recordar nada del otro usuario' },
    { id: 'context_continuity', label: 'Continuidad en misma sesion', question: 'que te dije hace un momento?', expected_fact: null, description: 'Dentro de la misma sesion, el agente recuerda mensajes anteriores' }
  ];

  let factsRes;
  try { factsRes = await AgentMemoryApiClient.getFacts(); } catch(e) { factsRes = { ok: false }; }
  const facts = factsRes.ok ? (factsRes.data?.data || []) : [];
  const factsByType = {};
  facts.forEach(f => { if (!factsByType[f.fact_type]) factsByType[f.fact_type] = []; factsByType[f.fact_type].push(f); });

  panel.innerHTML = `
    <div style="padding:16px">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px">
        <h3 style="margin:0;font-size:1rem">Tests de Recall (${testCases.length} cases)</h3>
        <div style="font-size:0.875rem;color:#8b949e">Facts cargados: ${facts.length}</div>
      </div>
      <p style="font-size:0.875rem;color:#8b949e;margin-bottom:8px">
        Estos tests verifican que Argemiro puede recordar hechos compilados del usuario.
        Status muestra si hay facts disponibles para cada test.
      </p>
      <div id="aq-badge-link" style="margin-bottom:16px"></div>
      <div style="display:flex;flex-direction:column;gap:8px">
        ${testCases.map(tc => {
          const hasFact = tc.expected_fact ? (factsByType[tc.expected_fact] || []).length > 0 : null;
          const factValue = tc.expected_fact && hasFact ? factsByType[tc.expected_fact][0].fact_value : null;
          const statusColor = hasFact === null ? '#8b949e' : hasFact ? '#3fb950' : '#d29922';
          const statusLabel = hasFact === null ? 'Manual' : hasFact ? 'Fact OK' : 'Sin fact';
          const statusIcon = hasFact === null ? '◯' : hasFact ? '✓' : '!';
          return `
            <div style="background:#161b22;border:1px solid #30363d;border-radius:6px;padding:12px">
              <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:6px">
                <div style="display:flex;align-items:center;gap:8px">
                  <span style="color:${statusColor};font-weight:bold;font-size:1rem">${statusIcon}</span>
                  <strong style="font-size:0.875rem">${esc(tc.label)}</strong>
                </div>
                <span style="background:${statusColor}22;color:${statusColor};padding:2px 8px;border-radius:4px;font-size:0.875rem;font-weight:600">${statusLabel}</span>
              </div>
              <div style="font-size:0.875rem;color:#8b949e;margin-bottom:4px">${esc(tc.description)}</div>
              <div style="display:flex;gap:12px;font-size:0.875rem">
                <span style="color:#58a6ff">Pregunta: <code>${esc(tc.question)}</code></span>
                ${factValue ? `<span style="color:#3fb950">Esperado: <strong>${esc(factValue)}</strong></span>` : ''}
              </div>
            </div>
          `;
        }).join('')}
      </div>
      <div style="margin-top:16px;padding:12px;background:#0d2818;border:1px solid #238636;border-radius:6px">
        <div style="font-size:0.875rem;font-weight:600;color:#3fb950;margin-bottom:6px">Como ejecutar los tests manualmente</div>
        <ol style="font-size:0.875rem;color:#8b949e;margin:0;padding-left:20px">
          <li>Ir a <a href="#/agents" style="color:#58a6ff">#/agents</a> → chat con Argemiro</li>
          <li>Enviar cada pregunta listada arriba</li>
          <li>Verificar que la respuesta contiene el valor esperado</li>
          <li>Para test de aislamiento: recargar pagina (F5) y preguntar</li>
        </ol>
      </div>
    </div>
  `;

  // Evaluate test results and sync to badge
  const autoTests = testCases.filter(tc => tc.expected_fact !== null);
  const passed = autoTests.filter(tc => (factsByType[tc.expected_fact] || []).length > 0);
  const testResults = {
    tests_passed: passed.length,
    tests_total: testCases.length,
    auto_passed: passed.length,
    auto_total: autoTests.length,
    manual_count: testCases.length - autoTests.length,
    test_names: passed.map(tc => tc.label),
    failed: autoTests.filter(tc => (factsByType[tc.expected_fact] || []).length === 0).map(tc => tc.label),
    evaluated_at: new Date().toISOString()
  };
  syncBadgeWithTests(testResults);
}

async function syncBadgeWithTests(testResults) {
  const el = document.getElementById('aq-badge-link');
  if (!el) return;
  try {
    const res = await AgentBadgeApiClient.list({ agentCode: 'ARGEMIRO-001', domain: 'memoria' });
    const badges = res.ok ? (res.data?.data?.items || res.data?.data || []) : [];
    if (badges.length === 0) { el.innerHTML = ''; return; }
    const b = badges[0];
    const badgeId = b.id || b.Id;

    // Sync: update validation_evidence with current test results. Desde nexo-api 709 escribir una insignia es solo
    // admin: el PUT solo lo intenta un admin y "Sincronizado" sale solo si el PUT dio ok (Ares #39931); antes se
    // pintaba con solo comparar los numeros, aunque el guardado fallara.
    const currentEvidence = b.validationEvidence || b.validation_evidence || {};
    const currentPassed = currentEvidence.tests_passed ?? -1;
    const cambio = currentPassed !== testResults.tests_passed && !!badgeId;
    let estadoSync = '';
    if (cambio && !esAdminDeLaApi(AppConfig.role)) {
      estadoSync = 'sin-permiso';
    } else if (cambio) {
      const put = await AgentBadgeApiClient.update(badgeId, {
        validationEvidence: { ...currentEvidence, ...testResults, source: 'Agent Quality tab Tests' }
      });
      estadoSync = put && put.ok ? 'ok' : 'fallo';
    }
    el.innerHTML = `
      <div style="padding:8px 12px;background:#161b22;border:1px solid #30363d;border-radius:6px;display:flex;align-items:center;gap:10px">
        <span class="task-tag" style="background:#a371f722;color:#a371f7">\uD83E\uDDE0 Badge vinculado</span>
        <strong style="font-size:0.875rem">${esc(b.name)}</strong>
        <span style="background:#a371f733;color:#a371f7;padding:2px 8px;border-radius:10px;font-size:0.875rem;font-weight:700">Lvl ${Math.floor(parseFloat(b.version || '1.0'))}</span>
        <span style="font-size:0.875rem;color:#8b949e">Tests: ${testResults.auto_passed}/${testResults.auto_total} auto + ${testResults.manual_count} manual</span>
        ${estadoSync === 'ok' ? '<span data-aq-sync="ok" style="font-size:0.875rem;color:#3fb950">Sincronizado</span>' : ''}
        ${estadoSync === 'sin-permiso' ? '<span data-aq-sync="sin-permiso" style="font-size:0.875rem;color:#8b949e">Sin sincronizar: actualizar la insignia es solo para admin</span>' : ''}
        ${estadoSync === 'fallo' ? '<span data-aq-sync="fallo" style="font-size:0.875rem;color:#d29922">No se pudo guardar el resultado en la insignia</span>' : ''}
        <a href="#/skills" style="font-size:0.875rem;color:#58a6ff;margin-left:auto">Ver en Badges \u2192</a>
      </div>`;
  } catch { el.innerHTML = ''; }
}

export async function renderAgentQuality(container) {
  containerRef = container;
  currentTab = 'convos';
  selectedSessionId = null;

  container.innerHTML = `
    <section class="artifact-view">
      <div class="artifact-header">
        <div>
          ${regresoHtml()}
          <h1>Agent Quality <span style="font-size:0.875rem;color:#8b949e;font-weight:normal">v1.1 F1-F4</span></h1>
          <p style="font-size:0.875rem;color:#8b949e;margin-top:2px">
            Conversaciones y memoria persistente de Argemiro &middot; conv.chat_memory &middot;
            <a href="#/missions" style="color:#58a6ff">ARES-AGENT-QUALITY-001</a>
          </p>
        </div>
      </div>

      <style>
        .aq-stat-card { background:#161b22;border:1px solid #30363d;border-radius:6px;padding:12px }
        .aq-stat-label { font-size:0.875rem;color:#8b949e;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:4px }
        .aq-stat-value { font-size:1.5rem;font-weight:600;color:#c9d1d9 }
        .aq-table { width:100%;border-collapse:collapse;font-size:0.875rem }
        .aq-table th { text-align:left;padding:8px;border-bottom:1px solid #30363d;color:#8b949e;font-weight:500;font-size:0.875rem;text-transform:uppercase }
        .aq-table td { padding:8px;border-bottom:1px solid #21262d }
        .aq-btn { background:#21262d;border:1px solid #30363d;color:#c9d1d9;padding:6px 12px;border-radius:4px;cursor:pointer;font-size:0.875rem }
        .aq-btn:hover { background:#30363d }
        .aq-btn-view { font-size:0.875rem;padding:4px 8px }
        .aq-tab { background:transparent;border:none;color:#8b949e;padding:10px 16px;cursor:pointer;font-size:0.875rem;border-bottom:2px solid transparent }
        .aq-tab:hover { color:#c9d1d9 }
        .aq-tab.active { color:#58a6ff;border-bottom-color:#58a6ff }
      </style>

      <div id="aq-tabs" style="display:flex;gap:4px;border-bottom:1px solid #30363d;margin-bottom:0;overflow-x:auto"></div>
      <div id="aq-content" style="background:#0d1117;border:1px solid #30363d;border-top:none;border-radius:0 0 6px 6px;min-height:400px"></div>
    </section>
  `;

  renderTabs();
  loadCurrentTab();
}
